// src/stores/documentChat.ts
import { Source } from "@/pages/EditTender/DocumentChat/Components/SourceCard";
import { groupDbMessagesIntoQa, normalizeSources } from "@/pages/EditTender/DocumentChat/utils";
import { AiChatMessage } from "@/graphql/__generated__/graphql";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/**
 * Represents one Q/A pair in the document chat along with supporting sources.
 */
export type ConversationItem = {
  /** User question */
  question: string;
  /** Assistant answer */
  answer: string;
  /** Document sources that supported the answer */
  sources: Source[];
};

type DocumentChatStore = {
  /**
   * Local, ephemeral Q/A pairs accumulated during the current session.
   * Keyed by aiImportId to keep conversations separate.
   * Persisted in sessionStorage to survive soft reloads until DB hydration arrives.
   */
  conversationsByImport: Record<string, ConversationItem[]>;

  /**
   * Q/A pairs hydrated from the backend (DB). Not persisted.
   */
  dbConversation: ConversationItem[];

  /** Returns the conversation to display for a specific aiImportId */
  getActiveConversation: (_aiImportId?: number | string) => ConversationItem[];

  /** Local message helpers (operate on conversation for specific aiImportId). */
  addMessage: (_item: ConversationItem, _aiImportId?: number | string) => void;
  updateLastAnswer: (_answer: string, _aiImportId?: number | string) => void;
  updateLastSources: (_sources: Source[], _aiImportId?: number | string) => void;
  clearConversation: (_aiImportId?: number | string) => void;
  /** Clear all conversations (used for logout). */
  clearAllConversations: () => void;

  /** DB conversation controls (operate on `dbConversation`). */
  setDbConversation: (_items: ConversationItem[]) => void;
  clearDbConversation: () => void;
  /** Build DB Q/A pairs from a flat list of DB messages. */
  hydrateFromDbMessages: (_msgs: AiChatMessage[]) => void;
};

export const useDocumentChatStore = create<DocumentChatStore>()(
  persist(
    (set, get) => ({
      conversationsByImport: {},
      dbConversation: [],

      getActiveConversation: (aiImportId) => {
        const state = get();
        const key = aiImportId != null ? String(aiImportId) : "default";
        // Return store conversation for this specific aiImportId
        return state.conversationsByImport[key] || [];
      },

      // Local conversation mutators
      addMessage: (item, aiImportId) =>
        set((state) => {
          const key = aiImportId != null ? String(aiImportId) : "default";
          const existing = state.conversationsByImport[key] || [];
          return {
            conversationsByImport: {
              ...state.conversationsByImport,
              [key]: [...existing, item],
            },
          };
        }),
      updateLastAnswer: (answer, aiImportId) =>
        set((state) => {
          const key = aiImportId != null ? String(aiImportId) : "default";
          const existing = state.conversationsByImport[key] || [];
          if (existing.length === 0) return state;
          const updatedConversation = [...existing];
          updatedConversation[updatedConversation.length - 1].answer = answer;
          return {
            conversationsByImport: {
              ...state.conversationsByImport,
              [key]: updatedConversation,
            },
          };
        }),
      updateLastSources: (sources, aiImportId) =>
        set((state) => {
          const key = aiImportId != null ? String(aiImportId) : "default";
          const existing = state.conversationsByImport[key] || [];
          const lastIndex = existing.length - 1;
          if (lastIndex < 0) return state;
          const updatedConversation = [...existing];
          updatedConversation[lastIndex] = {
            ...updatedConversation[lastIndex],
            sources: normalizeSources(sources as unknown as any[]),
          };
          return {
            conversationsByImport: {
              ...state.conversationsByImport,
              [key]: updatedConversation,
            },
          };
        }),
      clearConversation: (aiImportId) =>
        set((state) => {
          const key = aiImportId != null ? String(aiImportId) : "default";
          return {
            conversationsByImport: {
              ...state.conversationsByImport,
              [key]: [],
            },
          };
        }),
      clearAllConversations: () => set({ conversationsByImport: {} }),

      // DB conversation controls
      setDbConversation: (items) => set({ dbConversation: items }),
      clearDbConversation: () => set({ dbConversation: [] }),
      hydrateFromDbMessages: (msgs) =>
        set(() => ({ dbConversation: groupDbMessagesIntoQa(msgs) })),
    }),
    {
      name: "document-chat-store",
      storage: createJSONStorage(() => sessionStorage),
      // Only persist the local session conversations keyed by aiImportId. DB-hydrated data is transient.
      partialize: (state) => ({ conversationsByImport: state.conversationsByImport }),
    }
  )
);
